import type { UseQueryResult } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { QueryState } from "@/shared/ui/QueryState";
import type { Paged, ProblemCard, SolutionCard } from "@/shared/lib/apiTypes";
import { DefaultLayout } from "@/shared/ui/DefaultLayout";
import { CategoryIconsRow } from "@/public/features/home/ui/CategoryIconsRow";
import { RegisteredCompaniesRow } from "@/public/features/org/ui/RegisteredCompaniesRow";
import { ChevronRightIcon } from "@/shared/ui/icons/ChevronRightIcon";
import { CityIcon } from "@/shared/ui/icons/CityIcon";
import { InfoIcon } from "@/shared/ui/icons/InfoIcon";
import { EmptyStateCard } from "@/shared/ui/EmptyState";
import {
  EMPTY_ORG_CARD,
  EMPTY_PROBLEM_CARD,
} from "@/public/features/home/constants/homeEmpty";
import { ProblemIssueCard, SolutionListCard } from "@/shared/ui/IssueCard";
import { IssueIcon } from "@/public/features/home/ui/HomeVariants";
import { countsById } from "@/shared/lib/cardCounts";
import { profileTone } from "@/shared/lib/profileTone";
import { useCardLabels } from "@/shared/lib/cardLabels";
import { formatDate } from "@/shared/lib/format";
import { CARD_CATEGORY_MAX } from "@/shared/constants/cards"
import { CARD_SHELL_CLASS } from "@/shared/constants/cards"

type Props = {
  problems: UseQueryResult<Paged<ProblemCard>>;
  cards: UseQueryResult<Paged<SolutionCard>>;
};

export function TabletHome({ problems, cards }: Props) {
  const problemItems = problems.data?.items ?? [];
  const solutionItems = cards.data?.items ?? [];
  const problemCounts = countsById(problemItems, (p) => p.matchedSolutionCount);

  return (
    <DefaultLayout tree="pc" className="[--home-t-w:1000px]">
      <PcHero />
      <CategoryIconsRow />

      <PcSection
        title="최근 등록된 시민 문제"
        moreTo="/problems"
        query={problems}
        first
        empty={<EmptyStateCard {...EMPTY_PROBLEM_CARD} />}
      >
        {(labels) =>
          problemItems.map((p, i) => {
            const count = problemCounts.get(p.id);
            return (
              <Link
                key={p.id}
                className="flex h-[231px] text-inherit no-underline"
                to={`/problems/${p.id}`}
              >
                <ProblemIssueCard
                  categories={(p.categoryCodes ?? [])
                    .slice(0, CARD_CATEGORY_MAX)
                    .map(labels.categoryLabel)}
                  title={p.title}
                  summary={p.summary ?? undefined}
                  icon={<IssueIcon index={i} />}
                  metaItems={[formatDate(p.createdAt), p.region.name]}
                  {...(count !== undefined
                    ? { actionText: `적합카드 ${count}장`, actionMuted: count === 0 }
                    : {})}
                />
              </Link>
            );
          })
        }
      </PcSection>

      <div className="mx-auto w-[var(--home-t-w)] max-w-full">
        <RegisteredCompaniesRow />
      </div>

      <PcSection
        title="최근 등록된 해결카드"
        moreTo="/solutions"
        query={cards}
        empty={<EmptyStateCard {...EMPTY_ORG_CARD} />}
      >
        {(labels) =>
          solutionItems.map((c) => {
            return (
              <Link
                key={c.id}
                className={`flex h-[302px] ${CARD_SHELL_CLASS} text-inherit no-underline`}
                to={`/solutions/${c.id}`}
              >
                <SolutionListCard
                  profileIcon={<CityIcon size={20} />}
                  profileTone={profileTone(c.org.id)}
                  orgName={c.org.name}
                  categories={c.categoryCodes
                    .slice(0, CARD_CATEGORY_MAX)
                    .map(labels.categoryLabel)}
                  title={c.title}
                  summary={c.problemSolved}
                  {...(c.accessCondition !== null &&
                  c.accessCondition.length > 0
                    ? { notice: c.accessCondition, noticeIcon: <InfoIcon /> }
                    : {})}
                  metaItems={[]}
                />
              </Link>
            );
          })
        }
      </PcSection>
      <div className="h-[30px]" aria-hidden="true" />
    </DefaultLayout>
  );
}

function PcHero() {
  return (
    <section
      className="relative h-[385px] bg-[#e0cbf7] bg-[url('/illust/hero-background.svg')] bg-[length:1512px_385px] bg-top bg-no-repeat"
      style={{ backgroundPositionX: "center" }}
    >
      <div className="relative mx-auto h-full w-[var(--home-t-w)] max-w-full">
        <div className="absolute top-[100px] left-0 flex w-[701px] max-w-full flex-col gap-3.5">
          <h1 className="m-0 text-[38px] leading-[1.5] font-medium tracking-[-0.19px] whitespace-nowrap text-black-800">
            우리 동네의 문제,
            <br />
            함께 <span className="font-bold text-violet-500">해결해 봄 !</span>
          </h1>
          <p className="m-0 text-body-lg-reading font-medium text-black-600">
            시민의 문제를 등록하면
            <br />
            해결할 수 있는 기업을 찾아드려요.
          </p>
        </div>
      </div>
    </section>
  );
}

function PcSection<T>({
  title,
  moreTo,
  query,
  first = false,
  empty,
  children,
}: {
  title: string;
  moreTo: string;
  query: UseQueryResult<Paged<T>>;
  first?: boolean;
  empty: React.ReactNode;
  children: (labels: ReturnType<typeof useCardLabels>) => React.ReactNode;
}) {
  const labels = useCardLabels();
  const isEmpty = (query.data?.items ?? []).length === 0;

  return (
    <section className={`mx-auto w-[var(--home-t-w)] max-w-full ${first ? 'pt-8' : 'pt-[60px]'}`}>
      <div className="mb-[18px] flex items-center gap-3.5">
        <h2 className="m-0 min-w-px flex-1 text-body-lg font-semibold text-black-600">
          {title}
        </h2>
        {!isEmpty && (
          <Link
            className="ui-pressable inline-flex items-center gap-2 py-1 text-body-sm font-normal text-black-300 no-underline"
            to={moreTo}
          >
            더보기
            <ChevronRightIcon size={16} />
          </Link>
        )}
      </div>
      <QueryState
        isPending={query.isPending}
        error={query.error}
        isEmpty={isEmpty}
        empty={<div className="flex justify-center pt-6 pb-2">{empty}</div>}
      >
        <div className="grid grid-cols-3 items-stretch gap-x-4 gap-y-[18px]">
          {children(labels)}
        </div>
      </QueryState>
    </section>
  );
}
